import type { PointerEvent, ReactNode } from "react";
import { LayoutCanvasHandles } from "./LayoutCanvasHandles";
import type { CanvasRect, ResizeHandle } from "./layout-canvas-utils";

export type LayoutCanvasTileProps = {
  index: number;
  rect: CanvasRect;
  canvasWidth: number;
  canvasHeight: number;
  selected: boolean;
  disabled?: boolean;
  outOfBounds?: boolean;
  // The index badge is meaningless for a single-purpose tile (e.g. a QR
  // code box); hide it and render custom content in its place instead.
  showIndexBadge?: boolean;
  children?: ReactNode;
  onPointerDownTile: (
    index: number,
    event: PointerEvent<HTMLDivElement>,
  ) => void;
  onPointerDownHandle: (
    index: number,
    handle: ResizeHandle,
    event: PointerEvent<HTMLDivElement>,
  ) => void;
};

export function LayoutCanvasTile({
  index,
  rect,
  canvasWidth,
  canvasHeight,
  selected,
  disabled,
  outOfBounds,
  showIndexBadge = true,
  children,
  onPointerDownTile,
  onPointerDownHandle,
}: LayoutCanvasTileProps) {
  const borderClass = selected
    ? "border-2 border-blue-500 bg-blue-500/10"
    : outOfBounds
      ? "border border-dashed border-amber-500 bg-amber-500/10"
      : "border border-slate-400 bg-slate-400/10 hover:border-blue-400";

  return (
    <div
      className={`absolute box-border flex items-start justify-start ${
        disabled ? "cursor-default" : "cursor-move"
      } ${borderClass}`}
      style={{
        left: `${(rect.x / canvasWidth) * 100}%`,
        top: `${(rect.y / canvasHeight) * 100}%`,
        width: `${(rect.width / canvasWidth) * 100}%`,
        height: `${(rect.height / canvasHeight) * 100}%`,
        zIndex: selected ? 2 : 1,
      }}
      onPointerDown={(event) => onPointerDownTile(index, event)}
    >
      {children && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {children}
        </div>
      )}
      {showIndexBadge && (
        <span className="m-1 rounded bg-slate-900/70 px-1.5 py-0.5 text-xs font-medium text-white select-none">
          {index + 1}
        </span>
      )}
      {selected && (
        <LayoutCanvasHandles
          disabled={disabled}
          onPointerDownHandle={(handle, event) =>
            onPointerDownHandle(index, handle, event)
          }
        />
      )}
    </div>
  );
}
